import type { IModelConnection } from "@itwin/core-frontend";
import { QueryBinder, QueryRowFormat } from "@itwin/core-common";
import { Feature } from "../../domain/utilities/Feature";

const featureAspectSourceCache = new WeakMap<IModelConnection, FeatureAspectSource>();

interface FeatureAspectSource {
  schemaName: string;
  className: string;
  qualifiedClassName: string;
  featureDefinitionSelect: string;
  nameSelect: string;
}

const FEATURE_ASPECT_SOURCE_CANDIDATES: FeatureAspectSource[] = [
  {
    schemaName: "CifCommon",
    className: "FeatureWithNameAspect",
    qualifiedClassName: "CifCommon.FeatureWithNameAspect",
    featureDefinitionSelect: "f.DefinitionName AS FeatureDefinition",
    nameSelect: "f.FeatureName AS Name",
  },
  {
    schemaName: "CivilSchema_Published",
    className: "FeatureWithName_PresentationElementAspect",
    qualifiedClassName: "CivilSchema_Published.FeatureWithName_PresentationElementAspect",
    featureDefinitionSelect: "f.FeatureName AS FeatureDefinition",
    nameSelect: "f.FeatureName AS Name",
  },
];

async function resolveFeatureAspectSource(iModel: IModelConnection): Promise<FeatureAspectSource | undefined> {
  const cached = featureAspectSourceCache.get(iModel);
  if (cached) return cached;

  for (const candidate of FEATURE_ASPECT_SOURCE_CANDIDATES) {
    const reader = iModel.createQueryReader(
      `
        SELECT c.Name AS className
        FROM meta.ECClassDef c
        JOIN meta.ECSchemaDef s
          ON s.ECInstanceId = c.Schema.Id
        WHERE s.Name = ?
          AND c.Name = ?
      `,
      QueryBinder.from([candidate.schemaName, candidate.className]),
      { rowFormat: QueryRowFormat.UseJsPropertyNames }
    );

    for await (const row of reader) {
      if (String((row as any)?.className ?? "").trim()) {
        featureAspectSourceCache.set(iModel, candidate);
        return candidate;
      }
    }
  }

  return undefined;
}

/**
 * Returns the Feature definition for a given element, if any.
 */
export async function queryFeatureForElement(
  iModel: IModelConnection,
  elementId: string,
): Promise<Feature | undefined> {
  const featureAspectSource = await resolveFeatureAspectSource(iModel);
  if (!featureAspectSource) return undefined;

  const reader = iModel.createQueryReader(
    `
      SELECT
        ${featureAspectSource.featureDefinitionSelect},
        ${featureAspectSource.nameSelect}
      FROM ${featureAspectSource.qualifiedClassName} f
      WHERE f.Element.Id = ?
    `,
    QueryBinder.from([elementId]),
    { rowFormat: QueryRowFormat.UseECSqlPropertyNames }
  );

  for await (const row of reader) {
    const plain = row.toRow() as any;

    return new Feature({
      FeatureDefinition: plain.FeatureDefinition ?? "",
      Name: plain.Name ?? "",
      // No description column on the aspect, so default to empty.
      Description: "",
    });
  }

  // No supported feature aspect on this element
  return undefined;
}

